<template>
  <main class="page-operative page-operative-project page-operative-project-show">
    <div class="container-fluid">
      <div class="d-flex align-items-center justify-content-between flex-wrap flex-md-nowrap mb-5">
        <PageTitle>Dettagli progetto</PageTitle>

        <app-router-link v-if="userCanEdit" class="btn btn-outline-primary" :to="{ name: 'operative-company.projects.edit' }">
          Modifica
        </app-router-link>
      </div>

      <div v-if="loading">LOADING ...</div>
      <div v-else-if="errorLoadingData">Progetto da visualizzare non trovato</div>
      <div v-else-if="company && !userCanEdit">Utente non abilitato</div>
      <div v-else>
        <div class="row mt-3">
          <div class="col">
            <strong>Nome</strong>: {{ project.name }}
          </div>
          <div class="col-auto">
            <div>
              <strong>Stato</strong>:
              <template v-if="project.status === 'budgeted'">Previsto</template>
              <template v-else-if="project.status === 'accepted'">Accettato</template>
              <template v-else-if="project.status === 'started'">Avviato</template>
              <template v-else-if="project.status === 'completed'">Concluso</template>
            </div>

            <button v-if="project.status === 'budgeted'" class="btn btn-primary" @click.prevent="acceptProject">Imposta come accettato</button>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col"><strong>Operativo da</strong> {{ project.begin }} <strong>a</strong> {{ project.end }}</div>
        </div>

        <div class="overflow-auto report-texts-size">
          <OperativeTable dense sticky>
            <OperativeTableAggregateTable>
              <OperativeTableBodyHeader :years="aggregateTableHeadings" show-years with-total>Sommario</OperativeTableBodyHeader>
              <OperativeTableBodyRow :values="aggregateTableRows.operativo" with-total>
                Progetto in fase operativa
                <template #total>-</template>
                <template #cell-content="{ value }">{{ value ? 'Sì' : 'No' }}</template>
              </OperativeTableBodyRow>

              <OperativeTableBodyRow :values="aggregateTableRows.ricavo" with-total>
                Ricavi
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>

              <OperativeTableBodyRow :values="aggregateTableRows.costoEsterno" with-total>
                Costi esterni
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="aggregateTableRows.costoPersonale" with-total>
                Costi del personale
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>

              <OperativeTableBodyRow :values="aggregateTableRows.m1" with-total>
                M1
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="aggregateTableRows.costoStruttura" with-total><!-- Fixme -->
                Costi di struttura (quota parte)
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="aggregateTableRows.m2" with-total>
                M2
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>

              <OperativeTableBodyRow :values="aggregateTableRows.costoFigurato" with-total>
                Costi figurati
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="aggregateTableRows.m1f" with-total>
                M1 (compresi costi figurati)
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="aggregateTableRows.m2f" with-total>
                M2 (compresi costi figurati)
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableEmptyBodyRow :fixedNumColumns="aggregateTableHeadings.length + 2" />
            </OperativeTableAggregateTable>

            <tbody>
              <OperativeTableBodyHeader :years="aggregateTableHeadings" show-years with-total>Dettaglio costi esterni</OperativeTableBodyHeader>

              <OperativeTableBodyRow v-for="cost in costsRows" :values="cost.data.importo" :key="cost.name" with-total>
                {{ cost.name }}
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="aggregateTableRows.costoEsterno" with-total strong>
                Totale
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>

              <OperativeTableEmptyBodyRow :fixedNumColumns="aggregateTableHeadings.length + 2" />
            </tbody>

            <tbody>
              <OperativeTableBodyHeader :years="aggregateTableHeadings" show-years with-total>Dettaglio costi del personale</OperativeTableBodyHeader>

              <OperativeTableBodyRow v-for="cost in personnelCostsRows" :values="cost.data.importo" :key="cost.name" with-total>
                {{ cost.name }}
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="aggregateTableRows.costoPersonale" with-total strong>
                Totale
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>

              <OperativeTableEmptyBodyRow :fixedNumColumns="aggregateTableHeadings.length + 2" />
            </tbody>

            <tbody>
              <OperativeTableBodyHeader :years="aggregateTableHeadings" show-years with-total>Dettaglio costi figurati</OperativeTableBodyHeader>

              <OperativeTableBodyRow v-for="cost in imputedCostsRows" :values="cost.data.importo" :key="cost.name" with-total>
                {{ cost.name }}
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="aggregateTableRows.costoFigurato" with-total strong>
                Totale
                <template #total="{ total }">{{ total | money }}</template>
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
            </tbody>
          </OperativeTable>
        </div>
      </div>
    </div>
  </main>
</template>

<script>

import operativePageMixin from '@/mixins/operativePage.js';
import formatMoneyFilter from '@/filters/formatMoney.js';
import computedFinancialDataMixin from '@/mixins/computedFinancialData';

export default {
  mixins: [operativePageMixin, computedFinancialDataMixin],
  components: {
    PageTitle: () => import('@/views/components/Layout/LayoutOperative/PageTitle.vue'),
    OperativeTable: () => import('@/views/components/Operative/OperativeTable'),
    OperativeTableAggregateTable: () => import('@/views/components/Operative/OperativeTable/OperativeTableAggregateTable'),
    OperativeTableBodyHeader: () => import('@/views/components/Operative/OperativeTableBodyHeader'),
    OperativeTableBodyRow: () => import('@/views/components/Operative/OperativeTableBodyRow'),
    OperativeTableEmptyBodyRow: () => import('@/views/components/Operative/OperativeTableEmptyBodyRow'),
    AppRouterLink: () => import('@/views/components/Layout/AppRouterLink'),
  },
  filters: {
    money: formatMoneyFilter,
  },
  data () {
    return {
      project: null,
      loading: true,
      errorLoadingData: false,
    };
  },
  computed: {
    data () {
      if (!this.project || !this.project?.data || !this.project?.data?.length < 0) {
        return [];
      }

      return this.project.data;
    },
    aggregateTableHeadings () {
      if (!this.data.length < 0) {
        return [];
      }

      return this.convertToMonthsHeading(this.data);
    },
    aggregateTableRows () {
      if (!this.data.length < 0) {
        return [];
      }

      return this.convertToRowGroupAutomatically(this.data);
    },
    costsRows () {
      return (this.project?.listsCosts || []).map(cost => {
        cost.data = this.convertToRowGroupAutomatically(cost.data);

        return cost;
      });
    },
    imputedCostsRows () {
      return (this.project?.listsImputedCosts || []).map(cost => {
        cost.data = this.convertToRowGroupAutomatically(cost.data);

        return cost;
      });
    },
    personnelCostsRows () {
      return (this.project?.listsPersonnelCosts || []).map(cost => {
        cost.data = this.convertToRowGroupAutomatically(cost.data);

        return cost;
      });
    },
  },
  methods: {
    updateProject () {
      this.$api.getComputedProject(this.company.id, this.$route.params.projectId)
        .then(res => {
          this.project = res.data;
        })
        .catch(err => {
          this.errorLoadingData = true;
          this.$log.error(err);
        })
        .finally(() => {
          this.loading = false;
        })
      ;
    },
    acceptProject () {
      this.loading = true;

      this.$api.acceptProject(this.company.id, this.$route.params.projectId)
        .then(() => {
          this.updateProject();
        })
        .catch(err => {
          this.$log.error(err);
          this.loading = false;
        })
      ;
    },
  },
  mounted () {
    this.loading = true;
  },
  watch: {
    company: {
      immediate: true,
      handler: function () {
        if (!this.company) {
          return;
        }

        this.updateProject();
      },
    },
  },
};

</script>
